import React, { useState, useReducer, useEffect } from "react"
import {
  Modal,
  Button,
  Select,
  Form,
  Alert,
  Space,
  Divider,
  Typography,
  Card,
  Row,
  Col,
  Spin,
  Tooltip,
  Badge,
} from "antd"
import {
  FolderOpenOutlined,
  UploadOutlined,
  SaveOutlined,
  FileImageOutlined,
  CloseOutlined,
} from "@ant-design/icons"
import GridFiles from "components/Common/GridFilesAnt"
import FilePondUpload from "components/Common/FilePondUpload"
import { getCurrentUser, enumToArray } from "helpers/service_helper"
import { FileGasProjectTypeAdmin } from "models/types/file-gas-project-type-admin"

const { Title, Text } = Typography
const { Option } = Select

export const ProjectFiles = ({ rowData, mainProps, setErrorMessage }) => {
  const { lstGasProjectFiles } = mainProps
  const [files, setFiles] = useState([])
  const [formInput, setFormInput] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      idGasProject: rowData.id,
      fileTypeEnum: 2
    }
  )
  const [open, setOpen] = useState(false)
  const [fileGasProjectTypes, setFileGasProjectTypes] = useState([])
  
  useEffect(() => {
    setFileGasProjectTypes(enumToArray(FileGasProjectTypeAdmin))
  }, [])

  const handleSelectChange = (value) => {
    setFormInput({ fileTypeEnum: value })
  }

  const saveToDatabase = async (values) => {
    const { onAddFileGasProjectSingle } = mainProps
    let file = files[0].file
    const fileName = FileGasProjectTypeAdmin[values.fileTypeEnum]
    let formData = new FormData()
    formData.append("file", file, fileName + "." + file.name.split('.')[file.name.split('.').length - 1])
    formData.append("gasProjectId", JSON.stringify([values.idGasProject]))
    formData.append("name", "gas-" + fileName)
    formData.append("des", `Upload with-${getCurrentUser().sid}`)
    formData.append("fileTypeEnum", fileName)
    formData.append("FolderName", "GasProjects")
    formData.append("FileName", fileName + "." + file.name.split('.')[file.name.split('.').length - 1])
    formData.append("userId", getCurrentUser().sid)
    formData.append("toUserId", getCurrentUser().sid)
    await onAddFileGasProjectSingle(formData)
  }

  const handleSubmitForm = async () => {
    if (formInput.idGasProject.length !== 0 && files[0]) {
      await saveToDatabase(formInput)
    } else {
      setErrorMessage("برای ذخیره ابتدا فایل را بارگذاری کنید")
    }
  }

  const canUpload = rowData.projectLevelEnum !== 2 || getCurrentUser().role === 'Administrator'
  const isAdmin = getCurrentUser().role === 'Administrator'

  return (
    <>
      <Tooltip title="مدیریت فایل‌ها">
        <Button
          type="primary"
          ghost
          size="small"
          icon={<FolderOpenOutlined />}
          onClick={async () => {
            const { onGetProjectFiles } = mainProps
            await onGetProjectFiles(rowData.id)
            setOpen(true)
          }}
          style={{
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Badge count={lstGasProjectFiles?.length || 0} size="small" offset={[0, 0]}>
            <span style={{ marginLeft: 4 }}>فایل‌ها</span>
          </Badge>
        </Button>
      </Tooltip>

      <Modal
        open={open}
        title={
          <Space align="center">
            <FolderOpenOutlined style={{ color: "#1890ff", fontSize: 20 }} />
            <span>مدیریت فایل‌های پرونده: {rowData.fileNumber}</span>
          </Space>
        }
        onCancel={() => setOpen(false)}
        footer={null}
        width={720}
        centered
        destroyOnClose
        styles={{
          header: {
            borderBottom: "1px solid #f0f0f0",
            paddingBottom: 16,
          },
          body: {
            padding: "24px",
          },
        }}
      >
        {/* Alerts */}
        {mainProps.error && (
          <Alert
            message={mainProps.error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}
        {mainProps.success && (
          <Alert
            message={mainProps.success}
            type="success"
            showIcon
            closable
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}

        {/* Files Grid Section */}
        <Card
          size="small"
          title={
            <Space>
              <FileImageOutlined style={{ color: "#1890ff" }} />
              <Text strong>فایل‌های موجود</Text>
              <Badge 
                count={lstGasProjectFiles?.length || 0} 
                style={{ backgroundColor: "#52c41a" }}
              />
            </Space>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          styles={{ body: { padding: 16, maxHeight: 300, overflowY: "auto" } }}
        >
          <GridFiles lstGasProjectFiles={lstGasProjectFiles} props={mainProps} />
        </Card>

        {/* Upload Section */}
        {canUpload && (
          <Card
            size="small"
            title={
              <Space>
                <UploadOutlined style={{ color: "#52c41a" }} />
                <Text strong>آپلود فایل جدید</Text>
              </Space>
            }
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px dashed #d9d9d9",
            }}
            styles={{ body: { padding: 20 } }}
          >
            <Row gutter={[16, 16]}>
              {/* File Type Select */}
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  label={<Text strong>نوع فایل</Text>}
                  style={{ marginBottom: 16 }}
                >
                  <Select
                    value={formInput.fileTypeEnum || 2}
                    onChange={handleSelectChange}
                    size="large"
                    style={{ width: "100%", borderRadius: 8 }}
                    placeholder="نوع فایل را انتخاب کنید"
                  >
                    {fileGasProjectTypes?.map(row => (
                      <Option key={row.id} value={row.id}>
                        {mainProps.t("enums." + row.name)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* File Upload */}
              <Col xs={24}>
                <FilePondUpload
                  setFiles={setFiles}
                  maxFileSize="800KB"
                  allowMultiple={false}
                  maxFiles={1}
                  acceptedFileTypes={["image/png", "image/jpeg"]}
                  imagePreviewMaxHeight={600}
                  labelText={`${mainProps.t("enums." + FileGasProjectTypeAdmin[formInput.fileTypeEnum])} را اینجا بکشید`}
                  waterMark={false}
                />
              </Col>

              {/* Submit Button */}
              <Col xs={24}>
                <Divider style={{ margin: "12px 0" }} />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                  <Button
                    onClick={() => setOpen(false)}
                    icon={<CloseOutlined />}
                    style={{ borderRadius: 8 }}
                  >
                    بستن
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSubmitForm}
                    loading={mainProps.loading}
                    disabled={!isAdmin}
                    icon={<SaveOutlined />}
                    style={{
                      borderRadius: 8,
                      background: isAdmin 
                        ? "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)"
                        : undefined,
                      border: "none",
                    }}
                  >
                    ذخیره فایل
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>
        )}
      </Modal>
    </>
  )
}
